import type { LocalBusiness, WithContext } from 'schema-dts'
import type { EventDescription, Supplier } from '~/composables/types/event'

const MAX_SCHEMA_REVIEWS = 10
const MAX_SCHEMA_IMAGES = 5

const usePartnerSchema = (
  supplier: Supplier,
  events: EventDescription[]
): any => {
  const schema = ref<WithContext<LocalBusiness> | undefined>(undefined)
  const script = computed(() => ({
    key: 'partner-schema',
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const { locale } = useI18n()
  const fullLocalizedUrl = useFullLocalizedUrl()

  const getPriceRange = (): string | undefined => {
    const prices = events
      .map((event) => Number(eventGetters.getDefaultPriceAmount(event)) / 100)
      .filter((price) => Number.isFinite(price) && price > 0)
    if (!prices.length) return undefined

    const currency = eventGetters.getDefaultPriceCurrency(events[0]) || 'EUR'
    // Cents only when the price has them: "79 €", "71,10 €".
    const format = (price: number) =>
      new Intl.NumberFormat(convertLocaleIntoBCP47(locale.value), {
        style: 'currency',
        currency,
        minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
      }).format(price)
    const low = Math.min(...prices)
    const high = Math.max(...prices)
    return low === high ? format(low) : `${format(low)} – ${format(high)}`
  }

  const createSchema = (): WithContext<LocalBusiness> => {
    const profilePath = supplierGetters.getKonfettiProfilePath(supplier)
    const url = fullLocalizedUrl(profilePath)
    const businessId = getSchemaBusinessId(profilePath)
    // Postcode and city only, and no geo: coordinates would pinpoint the street
    // the public address leaves out.
    const address = supplierGetters.getPublicAddress(supplier)
    const zipcode = address.match(/^(\d{4,5})\s/)?.[1]

    const description = stripHtmlToPlainText(
      supplierGetters.getShortDescription(supplier) ||
        supplierGetters.getDescription(supplier)
    )
    const images = events
      .map((event) => eventGetters.getSchemaImages(event)[0])
      .filter(Boolean)
      .slice(0, MAX_SCHEMA_IMAGES)
    const logo = supplierGetters.getLogo(supplier)
    const priceRange = getPriceRange()

    const reviews = [...supplierGetters.getReviewsList(supplier)]
      .sort((a, b) =>
        String(reviewGetters.getCreatedAt(b) ?? '').localeCompare(
          String(reviewGetters.getCreatedAt(a) ?? '')
        )
      )
      .slice(0, MAX_SCHEMA_REVIEWS)
      .map((review) => {
        const datePublished = formatSchemaDate(
          reviewGetters.getCreatedAt(review)
        )

        return {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: reviewGetters.getReviewRating(review),
          },
          reviewBody: reviewGetters.getReview(review),
          author: {
            '@type': 'Person',
            name: reviewGetters.getAuthor(review),
          },
          ...(datePublished && { datePublished }),
        }
      })

    return {
      '@context': 'https://schema.org',
      '@type': supplierGetters.getSeoBusinessType(supplier) || 'LocalBusiness',
      ...(businessId && { '@id': businessId }),
      name: supplierGetters.getName(supplier),
      url,
      ...(description && { description }),
      ...(logo && { logo }),
      image: images.length ? images : [logo].filter(Boolean),
      ...(address && {
        address: getSchemaPostalAddress({ formatted: address, zipcode }),
      }),
      ...(priceRange && { priceRange }),
      ...(supplierGetters.getReviewsTotal(supplier) > 0 && {
        ...(reviews.length > 0 && { review: reviews }),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: supplierGetters.getReviewsAverage(supplier),
          bestRating: 5,
          worstRating: 1,
          ratingCount: supplierGetters.getReviewsTotal(supplier),
        },
      }),
    } as WithContext<LocalBusiness>
  }

  schema.value = createSchema()

  return {
    schema,
    script,
  }
}

export default usePartnerSchema
